import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { mq, Spacer, Spinner } from '@kvdbil/components';
import Step from './components/Step';
import { Fade } from '../Fade/Fade';
import StatusPageContainer from '../StatusPageContainer';
import { useCmsData } from '../../hooks/useCmsData';
import {
  ObjectPageDocument,
  ObjectPageQuery
} from '~/config/generated/graphql';
import { ObjectPageData } from '../../interfaces/store/CmsData';
import { useTranslation } from '~/Locale';

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 12;

  display: flex;
  flex-direction: column;
  flex-direction: column;

  background-color: ${({ theme }) => theme.colors.background.gray};
  width: 100%;
  padding: 1.5rem;

  ${mq('tablet')} {
    padding: 2rem 4rem;
  }
`;

const Progress = styled.div`
  background-color: ${({ theme }) => theme.colors.grayscaleToned.light4};
  border-radius: 1rem;
  height: 6px;
  width: 100%;
`;

const ProgressBar = styled.div<{ $width: number }>`
  background-color: ${({ theme }) => theme.colors.secondary.main};
  border-radius: 1rem;
  height: 100%;
  transition: width 0.7s cubic-bezier(0.645, 0.045, 0.355, 1);
  width: ${({ $width }) => $width || 100}%;
`;

interface Props {
  handleClose: () => void;
  onComplete?: () => void;
}

const Guide = ({ handleClose, onComplete }: Props) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  const { data: cmsData, fetching } = useCmsData<
    ObjectPageQuery,
    ObjectPageData
  >({
    storeKey: 'objectPage',
    query: ObjectPageDocument
  });

  const guideData = cmsData?.biddingGuide;
  const guideSteps = guideData?.steps ?? [];
  const totalSteps = guideSteps.length;

  const onClose = useCallback(() => {
    setCurrentStep(0);
    handleClose();
  }, [handleClose]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [onClose]);

  const displayGuideSteps =
    guideData &&
    guideSteps.map(guideStep => {
      return (
        <Fade visible key={guideStep.descriptionTitle} duration={400}>
          <Step
            {...guideStep}
            {...guideData}
            totalSteps={totalSteps}
            handleClose={onClose}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            onComplete={onComplete}
          />
        </Fade>
      );
    });

  const progressBarStep = currentStep + 1;
  const progressBarWidth = (progressBarStep / totalSteps) * 100;
  const displayCurrentStep = displayGuideSteps?.[currentStep];

  return (
    <Container role="region" aria-live="polite">
      {guideData && !fetching ? (
        <>
          {displayCurrentStep}
          <Spacer />
          <Progress>
            <ProgressBar
              role="progressbar"
              aria-valuenow={progressBarStep}
              aria-valuemin={1}
              aria-valuemax={4}
              aria-valuetext={t('Step %s of 4', progressBarStep)}
              $width={progressBarWidth}
            />
          </Progress>
        </>
      ) : (
        <StatusPageContainer>
          <Spinner color="neutral" />
        </StatusPageContainer>
      )}
    </Container>
  );
};

export default Guide;
